import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { X, Settings, Cookie, Shield, BarChart3, Megaphone } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_CONSENT_DATE_KEY = 'cookie_consent_date';

/**
 * Cookie-Banner mit DSGVO-konformen Kategorien
 * - Notwendig (immer aktiv)
 * - Statistik (PostHog EU)
 * - Marketing (optional, für künftige Erweiterungen)
 * 
 * Implementiert Google Consent Mode v2
 */
const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [statisticsEnabled, setStatisticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  useEffect(() => {
    window.__cookieBannerOpen = isVisible;
    window.dispatchEvent(new CustomEvent('cookie-banner', { detail: isVisible }));
  }, [isVisible]);

  useEffect(() => {
    // Google Consent Mode v2 - Default: Alles abgelehnt
    initGoogleConsentMode();
    
    // Prüfe ob Einwilligung bereits gegeben wurde
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setTimeout(() => setIsVisible(true), 1000);
    } else {
      const consentData = JSON.parse(consent);
      if (consentData.statistics) {
        loadPostHog();
        updateGoogleConsent(true, consentData.marketing || false);
      }
      if (consentData.marketing) {
        updateGoogleConsent(consentData.statistics || false, true);
      }
    }
  }, []);

  const initGoogleConsentMode = () => {
    // Google Consent Mode v2 - Default Consent (vor Nutzerinteraktion)
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    
    gtag('consent', 'default', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied',
      'functionality_storage': 'granted',
      'personalization_storage': 'denied',
      'security_storage': 'granted',
      'wait_for_update': 500
    });
  };

  const updateGoogleConsent = (statistics, marketing) => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'analytics_storage': statistics ? 'granted' : 'denied',
        'ad_storage': marketing ? 'granted' : 'denied',
        'ad_user_data': marketing ? 'granted' : 'denied',
        'ad_personalization': marketing ? 'granted' : 'denied'
      });
    }
  };

  const loadPostHog = () => {
    // Nur laden wenn nicht bereits geladen
    if (window.posthog && window.posthog.__loaded) return;
    
    // PostHog EU-Instanz (DSGVO-konform)
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug getPageViewId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
    
    window.posthog.init("phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs", {
      api_host: "https://eu.i.posthog.com",  // EU-Instanz für DSGVO
      person_profiles: "identified_only",
      // Session Recording deaktiviert für Datenschutz
      disable_session_recording: true,
      // Keine automatische Erfassung von sensiblen Daten
      autocapture: {
        dom_event_allowlist: ['click'],
        element_allowlist: ['button', 'a'],
        css_selector_allowlist: ['[data-track]']
      },
      // Keine Erfassung von IP-Adressen
      ip: false,
      // Keine persistenten Device-IDs
      persistence: 'localStorage',
      // Respektiere Do-Not-Track
      respect_dnt: true
    });
  };

  const saveConsent = (statistics, marketing) => {
    const consentData = {
      essential: true,
      statistics: statistics,
      marketing: marketing,
      timestamp: new Date().toISOString(),
      version: '2.0'  // Consent Mode v2
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    localStorage.setItem(COOKIE_CONSENT_DATE_KEY, new Date().toISOString());
    
    // Google Consent Mode aktualisieren
    updateGoogleConsent(statistics, marketing);
    
    if (statistics) {
      loadPostHog();
    } else if (window.posthog) {
      // PostHog deaktivieren wenn Statistik abgelehnt
      window.posthog.opt_out_capturing();
    }
    
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    setStatisticsEnabled(true);
    setMarketingEnabled(true);
    saveConsent(true, true);
  };

  const handleRejectAll = () => {
    setStatisticsEnabled(false);
    setMarketingEnabled(false);
    saveConsent(false, false);
  };

  const handleSaveSettings = () => {
    saveConsent(statisticsEnabled, marketingEnabled);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-500"
      role="dialog"
      aria-label="Cookie-Einstellungen"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm -z-10 md:hidden" />
      
      <div className="bg-white border-t border-slate-200 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-5">
          
          {!showSettings ? (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 bg-[#0E7490]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-5 h-5 text-[#0E7490]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1E3A5F] text-base">
                    Wir respektieren Ihre Privatsphäre
                  </h3>
                  <p className="text-slate-600 text-sm mt-0.5">
                    Wir nutzen Cookies für Funktionalität und Analyse.{' '}
                    <Link to="/datenschutz" className="text-[#0E7490] hover:underline">
                      Datenschutzerklärung
                    </Link>
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="h-11 px-5 rounded-full border-2 border-slate-300 text-slate-700 hover:bg-slate-100 font-medium text-sm order-2 sm:order-1"
                  data-testid="cookie-reject-btn"
                >
                  Nur Notwendige
                </Button>
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="h-11 px-5 rounded-full border-2 border-slate-300 text-slate-700 hover:bg-slate-100 font-medium text-sm order-3 sm:order-2"
                  data-testid="cookie-settings-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Einstellungen
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="h-11 px-5 rounded-full bg-[#0E7490] hover:bg-[#155E75] text-white font-medium text-sm order-1 sm:order-3"
                  data-testid="cookie-accept-btn"
                >
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#1E3A5F] text-lg">Cookie-Einstellungen</h3>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-sm text-slate-600">
                Hier können Sie Ihre Cookie-Präferenzen anpassen. Notwendige Cookies sind für 
                die Grundfunktionen erforderlich und können nicht deaktiviert werden.
              </p>
              
              <div className="space-y-3">
                {/* Notwendige Cookies - Immer aktiv */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-[#1E3A5F]">Notwendig</p>
                      <p className="text-sm text-slate-500">
                        Authentifizierung, Sicherheit, Grundfunktionen
                      </p>
                    </div>
                  </div>
                  <div className="relative">
                    <div className="w-12 h-7 bg-green-500 rounded-full cursor-not-allowed" title="Immer aktiv">
                      <div className="absolute right-1 top-1 w-5 h-5 bg-white rounded-full shadow" />
                    </div>
                  </div>
                </div>
                
                {/* Statistik Cookies - Optional */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <BarChart3 className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-[#1E3A5F]">Statistik</p>
                      <p className="text-sm text-slate-500">
                        Anonyme Nutzungsanalyse zur Verbesserung der Website (PostHog EU)
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setStatisticsEnabled(!statisticsEnabled)}
                    className={`relative w-12 h-7 rounded-full transition-colors ${
                      statisticsEnabled ? 'bg-[#0E7490]' : 'bg-slate-300'
                    }`}
                    role="switch"
                    aria-checked={statisticsEnabled}
                    aria-label="Statistik-Cookies aktivieren"
                    data-testid="cookie-statistics-toggle"
                  >
                    <div 
                      className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        statisticsEnabled ? 'right-1' : 'left-1'
                      }`} 
                    />
                  </button>
                </div>
                
                {/* Marketing Cookies - Optional (für Zukunft) */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Megaphone className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-[#1E3A5F]">Marketing</p>
                      <p className="text-sm text-slate-500">
                        Personalisierte Werbung und Remarketing (derzeit nicht verwendet)
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setMarketingEnabled(!marketingEnabled)}
                    className={`relative w-12 h-7 rounded-full transition-colors ${
                      marketingEnabled ? 'bg-[#0E7490]' : 'bg-slate-300'
                    }`}
                    role="switch"
                    aria-checked={marketingEnabled}
                    aria-label="Marketing-Cookies aktivieren"
                    data-testid="cookie-marketing-toggle"
                  >
                    <div 
                      className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        marketingEnabled ? 'right-1' : 'left-1'
                      }`} 
                    />
                  </button>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="h-11 px-5 rounded-full border-2 border-slate-300 text-slate-700 hover:bg-slate-100 font-medium text-sm flex-1"
                >
                  Alle ablehnen
                </Button>
                <Button
                  onClick={handleSaveSettings}
                  className="h-11 px-5 rounded-full bg-[#0E7490] hover:bg-[#155E75] text-white font-medium text-sm flex-1"
                  data-testid="cookie-save-btn"
                >
                  Auswahl speichern
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="h-11 px-5 rounded-full bg-[#1E3A5F] hover:bg-[#162d4a] text-white font-medium text-sm flex-1"
                >
                  Alle akzeptieren
                </Button>
              </div>
              
              <p className="text-xs text-slate-400 text-center">
                Ihre Einstellungen können Sie jederzeit über den Link in der Fußzeile ändern.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Export Funktion zum Zurücksetzen der Einwilligung (für Footer-Link)
export const resetCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  localStorage.removeItem(COOKIE_CONSENT_DATE_KEY);
  // PostHog opt-out wenn vorhanden
  if (window.posthog) {
    window.posthog.opt_out_capturing();
  }
  window.location.reload();
};

// Export Funktion zum Prüfen der Consent-Status
export const getCookieConsent = () => {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!consent) return null;
  return JSON.parse(consent);
};

export default CookieBanner;
